import { z } from "zod";

import type { TitleHolderType } from "../title-holders/models";
import type { ParticipationHealth } from "./models";

const OrganizationSchema = z.object({
  id: z.string(),
  name: z.string(),
  linkedProjectsViaVehicle: z.array(
    z.object({
      id: z.string(),
    }),
  ),
});

const TitleHolderOrganizationSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.custom<"company" | "individual" | "operator">(),
  organizationBankAccounts: z.array(
    z.object({
      bankAccountNumber: z.string(),
    }),
  ),
});

const ParticipationSchema = z.object({
  id: z.string(),
  health: z.custom<ParticipationHealth>().nullable(),
  review: z.string().nullable(),
  reviewDate: z.string().nullable(),
  analyst: z.object({ id: z.string() }).nullable(),
  organization: OrganizationSchema,
  titleHolders: z.array(
    z.object({
      id: z.string(),
      organization: TitleHolderOrganizationSchema,
      ownerOrganization: TitleHolderOrganizationSchema,
      titlesCount: z.number(),
      type: z.custom<TitleHolderType>(),
    }),
  ),
});

type ParticipationType = z.infer<typeof ParticipationSchema>;

export function isParticipation(data: unknown): data is ParticipationType {
  return ParticipationSchema.safeParse(data).success;
}

export function isParticipations(data: unknown): data is ParticipationType[] {
  return ParticipationSchema.array().safeParse(data).success;
}

export function assertIsParticipation(
  data: unknown,
): asserts data is ParticipationType {
  const result = ParticipationSchema.safeParse(data);
  if (!result.success) {
    throw new Error(`Invalid Participation data: ${result.error}`);
  }
}

export function assertIsParticipations(
  data: unknown,
): asserts data is ParticipationType[] {
  const result = ParticipationSchema.array().safeParse(data);
  if (!result.success) {
    throw new Error(`Invalid Participations[] data: ${result.error}`);
  }
}
